import { Link } from "react-router-dom";
import { Container, SectionHeader, PullQuote, CTAStrip, ImageBlock } from "@/components/Sections";
import { IMAGES } from "@/lib/content";
import { ArrowRight } from "lucide-react";

const MODULES = [
  {
    number: "01",
    title: "Understanding Native Seed Systems",
    items: [
      "The role of seed in restoration and rehabilitation",
      "Why seed supply matters",
      "Restoration project life cycles",
      "Seed demand across carbon, biodiversity and rehabilitation projects",
    ],
  },
  {
    number: "02",
    title: "Seed Collection Planning",
    items: [
      "Understanding target species",
      "Seasonal planning",
      "Collection permits and approvals",
      "Working with Traditional Owners and cultural protocols",
      "Site selection and collection planning",
    ],
  },
  {
    number: "03",
    title: "Provenance and Genetics",
    items: [
      "Why provenance matters",
      "Local adaptation and climate resilience",
      "Genetic diversity considerations",
      "Risks associated with inappropriate seed sourcing",
      "Future climate and restoration planning",
    ],
  },
  {
    number: "04",
    title: "Field Seed Collection Techniques",
    items: [
      "Species identification",
      "Assessing seed maturity",
      "Collection methods for different species",
      "Equipment and safety requirements",
      "Recording field observations",
    ],
  },
  {
    number: "05",
    title: "Seed Handling and Storage",
    items: [
      "Cleaning and processing",
      "Seed storage principles",
      "Maintaining seed viability",
      "Documentation and traceability",
    ],
  },
  {
    number: "06",
    title: "Data Collection and Monitoring",
    items: [
      "Collection records",
      "GPS and mapping",
      "Provenance information",
      "Environmental observations",
      "Digital field data systems",
    ],
  },
  {
    number: "07",
    title: "Restoration Readiness",
    items: [
      "Understanding species lists",
      "Restoration project planning",
      "Seed supply forecasting",
      "Working with nurseries and restoration practitioners",
    ],
  },
];

const OUTCOMES = [
  "Native seed collection",
  "Seed handling and storage",
  "Provenance documentation",
  "Field data collection",
  "Restoration planning",
  "Biodiversity monitoring",
  "Environmental reporting",
];

const CAREERS = [
  "Native Seed Collector",
  "Seed Bank Coordinator",
  "Restoration Technician",
  "Biodiversity Monitoring Officer",
  "Environmental Field Officer",
  "Ranger Program Coordinator",
  "Restoration Supervisor",
  "Carbon and Nature Repair Project Support Roles",
];

const DELIVERY = [
  "On-Country workshops",
  "Ranger training programs",
  "Community seed collection intensives",
  "Restoration readiness training",
  "Custom training for Indigenous organisations and land management groups",
];

export default function SeedTraining() {
  return (
    <div data-testid="page-seed-training" className="bg-[#F6F1E6]">
      {/* HERO */}
      <section className="relative pt-40 md:pt-52 pb-24 md:pb-32 border-b border-[#1F2A1B]/10">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
            <div className="lg:col-span-7">
              <p className="overline mb-6">Pathway · Native Seed & Restoration</p>
              <h1
                data-testid="seed-title"
                className="display-xxl text-5xl md:text-7xl lg:text-[104px] mb-10 leading-[1.02]"
              >
                Restoration
                <br />
                begins with{" "}
                <span className="italic text-[#3F5132]">seed.</span>
              </h1>
              <p className="text-[#4F5A48] text-lg md:text-xl max-w-2xl leading-relaxed mb-10">
                Across Australia, demand for native seed is growing rapidly. Carbon
                projects, mine rehabilitation, biodiversity conservation, Nature
                Repair projects and ecological restoration all rely on
                high-quality native seed — collected from the right species, in
                the right place, at the right time.
              </p>
              <Link to="/contact" data-testid="seed-cta-hero" className="btn-primary">
                Bring this training to your program
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <ImageBlock
                src={IMAGES.regrowth}
                alt="Native regrowth emerging on Country"
                caption="Seed × Country × Capability"
                className="aspect-[4/5]"
                testid="seed-hero-image"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32 border-b border-[#1F2A1B]/10">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <SectionHeader
                label="The opportunity"
                title="A growing"
                italic="seed shortage."
                className="mb-0"
              />
            </div>
            <div className="lg:col-span-7 space-y-6 text-[#4F5A48] text-lg leading-relaxed">
              <p>
                Many regions are already experiencing seed shortages, creating a
                growing demand for people with practical skills in seed
                collection, seed handling and restoration planning.
              </p>
              <p>
                This training pathway introduces Indigenous rangers and
                environmental practitioners to the foundations of native seed
                collection and restoration practice — grounded in cultural
                protocol, local knowledge and field-based experience.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* MODULES */}
      <section className="py-24 md:py-32 border-b border-[#1F2A1B]/10 bg-[#EBE3D0]">
        <Container>
          <SectionHeader
            number="Curriculum"
            label="What you will learn"
            title="Seven modules,"
            italic="taught on Country."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2A1B]/10 border border-[#1F2A1B]/10">
            {MODULES.map((m, idx) => (
              <article
                key={m.number}
                data-testid={`seed-module-${m.number}`}
                className={`bg-[#F6F1E6] p-8 md:p-10 ${
                  idx === MODULES.length - 1 && MODULES.length % 2 === 1
                    ? "md:col-span-2"
                    : ""
                }`}
              >
                <div className="flex items-baseline gap-5 mb-6">
                  <span className="section-number">— Module {m.number}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1F2A1B] mb-6 leading-tight">
                  {m.title}
                </h3>
                <ul className="space-y-0">
                  {m.items.map((it, i) => (
                    <li
                      key={it}
                      className="flex items-baseline gap-4 py-3 border-b border-[#1F2A1B]/10"
                    >
                      <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[#1F2A1B] text-base">{it}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* LEARNING OUTCOMES + CAREERS */}
      <section className="py-24 md:py-32 border-b border-[#1F2A1B]/10">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <div data-testid="seed-outcomes">
              <p className="overline mb-5">Learning outcomes</p>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] text-[#1F2A1B] mb-10">
                Practical skills
                <br />
                <span className="italic text-[#3F5132]">in seven areas.</span>
              </h2>
              <ul className="space-y-0">
                {OUTCOMES.map((o, i) => (
                  <li
                    key={o}
                    className="flex items-baseline gap-4 py-4 border-b border-[#1F2A1B]/10"
                  >
                    <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#1F2A1B] text-base md:text-lg">{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="seed-careers">
              <p className="overline mb-5">Career pathways</p>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] text-[#1F2A1B] mb-10">
                Roles this
                <br />
                <span className="italic text-[#3F5132]">training supports.</span>
              </h2>
              <ul className="space-y-0">
                {CAREERS.map((c, i) => (
                  <li
                    key={c}
                    className="flex items-baseline gap-4 py-4 border-b border-[#1F2A1B]/10 group"
                  >
                    <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#1F2A1B] text-base md:text-lg">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* WHY IT MATTERS */}
      <section className="py-24 md:py-32 border-b border-[#1F2A1B]/10 bg-[#EBE3D0]">
        <Container>
          <SectionHeader
            label="Why it matters"
            title="Native seed is more"
            italic="than a restoration input."
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-7 space-y-6 text-[#4F5A48] text-lg leading-relaxed">
              <p>
                It carries genetics, adaptation, ecological function and
                connection to Country.
              </p>
              <p>
                As investment in restoration, biodiversity and carbon projects
                continues to grow, the need for skilled seed practitioners will
                continue to increase.
              </p>
              <p>
                Building Indigenous capability in native seed systems helps
                ensure that restoration outcomes are stronger, local knowledge is
                valued, and more environmental investment remains connected to
                Country.
              </p>
            </div>
            <div className="lg:col-span-5">
              <PullQuote testid="seed-quote">
                Seed carries genetics, adaptation, ecological function — and
                connection to Country.
              </PullQuote>
            </div>
          </div>
        </Container>
      </section>

      {/* DELIVERY OPTIONS */}
      <section className="py-24 md:py-32 border-b border-[#1F2A1B]/10">
        <Container>
          <SectionHeader
            label="Delivery options"
            title="How the training"
            italic="can be delivered."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2A1B]/10 border border-[#1F2A1B]/10">
            {DELIVERY.map((d, i) => (
              <article
                key={d}
                data-testid={`seed-delivery-${i}`}
                className="bg-[#F6F1E6] p-10 min-h-[200px] flex flex-col justify-between"
              >
                <span className="section-number">
                  — Option {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-serif text-2xl md:text-3xl text-[#1F2A1B] leading-snug mt-6">
                  {d}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <CTAStrip
        heading="Bring native seed training to your program."
        body="We work with ranger programs, Aboriginal corporations and Traditional Owner organisations to deliver field-based seed training that connects technical capability with cultural protocol and Country."
        label="Start a conversation"
        testid="seed-cta-strip"
      />
    </div>
  );
}
